import type { ApiInvite } from '#/api/invite/types/ApiInvite';
import { Divider } from '#/components/Divider/Divider';
import type { FormUsageContext } from '#/components/Form/Form';
import { Form } from '#/components/Form/Form';
import type {
  FormMapErrorFunction,
  FormSubmitFunction,
} from '#/components/Form/FormContext';
import { GeneralError } from '#/components/Form/GeneralError';
import { InputField } from '#/components/Form/InputField';
import { LocationField } from '#/components/Form/LocationField';
import { RecaptchaField } from '#/components/Form/RecaptchaField';
import { SubmitButton } from '#/components/Form/SubmitButton/SubmitButton';
import { InviteMessage } from '#/components/InviteMessage/InviteMessage';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { AppleOAuthButton } from '#/components/OAuthButton/AppleOAuthButton';
import { FacebookOAuthButton } from '#/components/OAuthButton/FacebookOAuthButton';
import { GoogleOAuthButton } from '#/components/OAuthButton/GoogleOAuthButton';
import { BodySmall } from '#/components/Text/BodySmall';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';

import {
  SignupForm_Box_1,
  SignupForm_Box_2,
  SignupForm_Box_3,
  SignupForm_Box_4,
  SignupForm_Box_5,
} from './SignupForm.styled';
import { SignupFormLoginLink } from './SignupFormLoginLink';

// TODO: add proper types to SignupFormValues
export type SignupFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  location: SearchLocation | null;
  recaptchaKey: number;
  gRecaptchaResponse: unknown;
};

type Props = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  initialValues: Partial<SignupFormValues>;
  invite?: ApiInvite | null | undefined;
  showLoginModal?: (email: string) => void;
  getLoginUrlAndState: (email?: string) => {
    to: string;
    state: Record<string, unknown>;
  };
  getThirdPartyLoginUrl: (
    provider: 'facebook' | 'google' | 'apple',
  ) => Promise<string> | string;
  onSubmit: FormSubmitFunction<SignupFormValues>;
  userCameFromMembershipPage: boolean;
};

export function SignupForm({
  analyticsTitle,
  usageContext,
  initialValues,
  invite,
  showLoginModal,
  getLoginUrlAndState,
  getThirdPartyLoginUrl,
  onSubmit,
  userCameFromMembershipPage,
}: Props) {
  const { i18n } = useI18n();

  const emailErrorWithLogin = (email: string) =>
    i18n('Email already in use - Please {{link}}login{{/link}}', {
      link: (
        <SignupFormLoginLink
          formUsageContext={usageContext}
          email={email}
          getLoginUrlAndState={getLoginUrlAndState}
          showLoginModal={showLoginModal}
          qaId="email-error-login-link"
        />
      ),
    });

  const mapError: FormMapErrorFunction<SignupFormValues> = ({
    name,
    value,
    type,
    description,
  }) =>
    name === 'email' && type === 'alreadyInUse'
      ? emailErrorWithLogin(typeof value === 'string' ? value : '')
      : description;

  return (
    <Form
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      qaId="signup-form"
      initialValues={initialValues}
      mapError={mapError}
      onSubmit={onSubmit}
      render={({ values }) => (
        <>
          {invite && (
            <BodySmall style={{ marginTop: 3, marginBottom: 4 }}>
              {invite.hooks.map((hook) => (
                <InviteMessage
                  key={hook.hookType}
                  invite={invite}
                  hook={hook}
                />
              ))}
            </BodySmall>
          )}

          {!userCameFromMembershipPage && (
            <SignupForm_Box_5>
              <BodySmall style={{ textAlign: 'center' }}>
                {i18n('Already have an account?')}{' '}
                <SignupFormLoginLink
                  qaId="signup-form-login-link"
                  email={values.email}
                  getLoginUrlAndState={getLoginUrlAndState}
                  showLoginModal={showLoginModal}
                  formUsageContext={usageContext}
                >
                  {i18n('Log In')}
                </SignupFormLoginLink>
              </BodySmall>
            </SignupForm_Box_5>
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <GoogleOAuthButton getUrl={() => getThirdPartyLoginUrl('google')} />
            <FacebookOAuthButton
              getUrl={() => getThirdPartyLoginUrl('facebook')}
            />

            <AppleOAuthButton getUrl={() => getThirdPartyLoginUrl('apple')} />
          </div>

          <Divider marginTop={24} marginBottom={24} />

          <GeneralError />

          {!userCameFromMembershipPage && (
            <div
              style={{ display: 'flex', flexWrap: 'wrap', marginInline: -12 }}
            >
              <SignupForm_Box_4>
                <InputField
                  dataModalFocus={usageContext === 'modal' ? 'true' : undefined}
                  name="firstName"
                  label={i18n('First Name')}
                  placeholder={i18n('Casey')}
                />
              </SignupForm_Box_4>

              <SignupForm_Box_3>
                <InputField
                  name="lastName"
                  label={i18n('Last Name')}
                  placeholder={i18n('Smith')}
                />
              </SignupForm_Box_3>
            </div>
          )}

          {!userCameFromMembershipPage && (
            <InputField
              name="email"
              type="email"
              label={i18n('Email')}
              placeholder={i18n('casey.smith@example.com')}
            />
          )}

          <InputField
            name="password"
            type="password"
            label={i18n('Password')}
            placeholder={i18n('Password')}
          />

          {!userCameFromMembershipPage && (
            <LocationField
              name="location"
              label={
                /* TRANSLATORS: where is the user located? e.g. New York */
                i18n('Location')
              }
              customErrorMatch={(errors) =>
                errors?.latitude ?? errors?.longitude
              }
            />
          )}

          <RecaptchaField />

          <SignupForm_Box_2>
            <SubmitButton fullWidth size="large">
              {i18n('Sign Up')}
            </SubmitButton>
          </SignupForm_Box_2>

          <SignupForm_Box_1>
            {i18n(
              'By continuing, you agree to Idealist’s {{terms}}Terms of Service{{/terms}}, and acknowledge our {{privacy}}Privacy Policy{{/privacy}}.',
              {
                terms: (
                  <StyledNativeLinkWithRef
                    href={i18n('https://www.idealist.org/en/terms-of-service')}
                    target="_blank"
                  ></StyledNativeLinkWithRef>
                ),

                privacy: (
                  <StyledNativeLinkWithRef
                    href={i18n('https://www.idealist.org/en/privacy-policy')}
                    target="_blank"
                  ></StyledNativeLinkWithRef>
                ),
              },
            )}
          </SignupForm_Box_1>
        </>
      )}
    />
  );
}
